import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { find, toNumber } from "lodash";
import { Card, Empty, Spin, Tag } from "antd";
import { AICC_CONVERSATION_TYPE } from "constants/aicc";
import {
  AiccConversationAudio,
  AiccConversationDetail as AiccConversationDetailModel,
  AiccSubUserDetail,
} from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { useAICCStore } from "../../../useAICCStore";
import { SubUserDetailTab } from "../SubUserDetail/constants";
import PreviewTag from "../PreviewTag";
import Context from "./Context";
import Transcript from "./Transcript";
import Feedback from "./Feedback";
import styles from "./ConversationDetail.module.scss";

function ConversationDetail() {
  const { subUserId, conversationId, languageId: languageIdParam } = useParams<{
    subUserId: string;
    conversationId: string;
    languageId: string;
  }>();
  const languageId = toNumber(languageIdParam);
  const { scrollToTranscript } = useLocation<{ scrollToTranscript?: boolean }>().state ?? {};
  const transcriptRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  const setSubUserDetailTab = useAICCStore((state) => state.setSubUserDetailTab);

  const [isLoading, setIsLoading] = useState(false);
  const [detail, setDetail] = useState<AiccConversationDetailModel>();
  const [audio, setAudio] = useState<AiccConversationAudio>({});
  const [subUser, setSubUser] = useState<AiccSubUserDetail | undefined>(() => {
    const cache = useAICCStore.getState().cachedSubUserDetail;
    return cache?.id === subUserId ? cache : undefined;
  });

  useEffect(() => {
    let isStale = false;
    aiccAPI
      .fetchAiccSubUserDetail(subUserId)
      .then((subUserDetail) => !isStale && setSubUser(subUserDetail))
      .catch(() => undefined);

    return () => {
      isStale = true;
    };
  }, [subUserId]);

  useEffect(() => {
    if (!languageId) {
      return;
    }

    setIsLoading(true);
    Promise.all([
      aiccAPI.fetchAiccConversationDetail(subUserId, languageId, conversationId).catch(() => undefined),
      aiccAPI.fetchAiccConversationAudio(subUserId, languageId, conversationId).catch(() => ({})),
    ])
      .then(([conversationDetail, conversationAudio]) => {
        setDetail(conversationDetail);
        setAudio(conversationAudio);

        if (conversationDetail) {
          setSubUserDetailTab({
            subUserId,
            tab:
              conversationDetail.type === AICC_CONVERSATION_TYPE.lessonReview
                ? SubUserDetailTab.LessonReview
                : SubUserDetailTab.ScenarioConversation,
          });
        }
      })
      .finally(() => setIsLoading(false));
  }, [subUserId, conversationId, languageId, setSubUserDetailTab]);

  useEffect(() => {
    if (!scrollToTranscript || isLoading || !detail || hasScrolledRef.current) {
      return;
    }

    hasScrolledRef.current = true;
    const timer = window.setTimeout(
      () => transcriptRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      0
    );

    return () => window.clearTimeout(timer);
  }, [scrollToTranscript, isLoading, detail]);

  const languageName = find(subUser?.studyLanguages, { id: languageId })?.name;

  return (
    <Spin spinning={isLoading}>
      <Card
        title={
          <div className={styles.pageTitle}>
            <div className={styles.pageTags}>
              {subUser?.isPreview && <PreviewTag />}
              <span>{conversationId}</span>
            </div>
            {!!languageName && <Tag className={styles.languageTag}>{languageName}</Tag>}
          </div>
        }
        className="page-container"
      >
        {!languageId || (!isLoading && !detail) ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            <div className={styles.sectionTitle}>Context</div>
            <div className={styles.section}>
              <Context detail={detail} />
            </div>

            <div ref={transcriptRef} className={styles.sectionTitle}>
              Transcript
            </div>
            <div className={styles.section}>
              <Transcript messages={detail?.messages ?? []} audio={audio} />
            </div>

            <div className={styles.sectionTitle}>Feedback</div>
            <Feedback feedback={detail?.feedback ?? { achievements: [], suggestions: [] }} />
          </>
        )}
      </Card>
    </Spin>
  );
}

export default ConversationDetail;
